import { Stack, Text } from '@mantine/core';
import * as React from 'react';
import { useMemo } from 'react';
import { type MantineColumn, MantineTable } from '../../../../../../react-commons/components/table/MantineTable';
import { ValidationEntrySeverity } from '../../../../../../react-commons/components/ValidationEntrySeverity';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { formatToPascalCase } from '../../../../../../react-commons/utils/utilFunctions';
import { BackgroundImportTaskResult } from '../../../../resource/services/backgroundTaskResource';
import { ValidationResourceResultComponent } from './ValidationResourceResultComponent';

export const ProjectImportValidationResultComponent = ({
                                                           importTaskResult,
                                                       }: {
    importTaskResult: BackgroundImportTaskResult;
}) => {
    const columns = useMemo<
        Array<
            MantineColumn<{
                artifactType: string;
                total: number;
                notes: number;
                warnings: number;
                criticals: number;
                fatal: number;
            }>
        >
    >(() => {
        return [
            {
                type: 'string',
                accessorKey: 'artifactType',
                header: 'Artifact Type',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String
                        nullable={false}
                        readOnly={true}
                        value={formatToPascalCase(row.original.artifactType)}
                    />
                ),
            },
            {
                type: 'number',
                accessorKey: 'notes',
                header: 'Notes',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.Number nullable={false} readOnly={true} value={row.original.notes} />
                ),
            },
            {
                type: 'number',
                accessorKey: 'warnings',
                header: 'Warnings',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.Number nullable={false} readOnly={true} value={row.original.warnings} />
                ),
            },
            {
                type: 'number',
                accessorKey: 'criticals',
                header: 'Criticals',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.Number nullable={false} readOnly={true} value={row.original.criticals} />
                ),
            },
            {
                type: 'number',
                accessorKey: 'fatal',
                header: 'Fatal',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.Number nullable={false} readOnly={true} value={row.original.fatal} />
                ),
            },
            {
                type: 'number',
                accessorKey: 'total',
                header: 'Total Created',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.Number nullable={false} readOnly={true} value={row.original.total} />
                ),
            },
        ];
    }, []);
    return (
        <Stack>
            {importTaskResult.successful && importTaskResult.numberOfArtifactsCreated && (
                <div style={{ display: 'inline-flex' }}>
                    <Text style={{ minWidth: 200 }}>Total Imported Artifacts</Text>
                    <ControlledInputs.Number
                        nullable={false}
                        readOnly={true}
                        value={importTaskResult.numberOfArtifactsCreated}
                    />
                </div>
            )}
            {!importTaskResult.successful && importTaskResult.erroneousArtifactType && (
                <div style={{ display: 'inline-flex' }}>
                    <Text style={{ minWidth: 200 }}>Erroneous Artifact Type</Text>
                    <ControlledInputs.String
                        nullable={false}
                        style={{ minWidth: 350 }}
                        readOnly={true}
                        value={formatToPascalCase(importTaskResult.erroneousArtifactType)}
                    />
                </div>
            )}
            {!importTaskResult.successful && importTaskResult.erroneousCreationResult && (
                <ValidationResourceResultComponent
                    maxSeverity={importTaskResult.erroneousCreationResult.maxSeverity ?? ValidationEntrySeverity.NOTE}
                    resourceValidation={importTaskResult.erroneousCreationResult.validationResult}
                    persistableTypeGroup={importTaskResult.erroneousArtifactType ?? ''}
                />
            )}

            {importTaskResult.successful && importTaskResult.artifactsCreatedCountMap && (
                <MantineTable
                    columns={columns}
                    data={Object.keys(importTaskResult.artifactsCreatedCountMap).map((value) => ({
                        artifactType: value,
                        notes: importTaskResult.artifactsCreatedCountMap![value].notes,
                        warnings: importTaskResult.artifactsCreatedCountMap![value].warnings,
                        criticals: importTaskResult.artifactsCreatedCountMap![value].criticals,
                        fatal: importTaskResult.artifactsCreatedCountMap![value].fatal,
                        total: importTaskResult.artifactsCreatedCountMap![value].total,
                    }))}
                    label={''}
                    enableActions={false}
                />
            )}
        </Stack>
    );
};
